import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Checkbox,
  FormControlLabel,
  Avatar,
  Badge,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Group as GroupIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface Angajat {
  id: string;
  nume: string;
  prenume: string;
  functie: string;
  parohieId?: string;
  parohie?: string;
  email: string;
  telefon: string;
  adresa: string;
  dataAngajare: string;
  salariu: number;
  status: 'activ' | 'inactiv' | 'concediat';
  specializare?: string;
  studii?: string;
  observatii?: string;
  managerId?: string;
  manager?: string;
}

const Angajati: React.FC = () => {
  const { hasPermission, user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFunctie, setFilterFunctie] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAngajat, setSelectedAngajat] = useState<Angajat | null>(null);
  const [showOnlyMyAngajati, setShowOnlyMyAngajati] = useState(false);

  // Mock data pentru angajați
  const [angajati] = useState<Angajat[]>([
    {
      id: '1',
      nume: 'Popescu',
      prenume: 'Gheorghe',
      functie: 'Paroh',
      parohieId: 'parohia1',
      parohie: 'Biserica Sf. Nicolae',
      email: 'gheorghe.popescu@episcopia.ro',
      telefon: '0721234567',
      adresa: 'Str. Principală nr. 1, București',
      dataAngajare: '2020-01-15',
      salariu: 3500,
      status: 'activ',
      specializare: 'Teologie',
      studii: 'Licență în Teologie',
      observatii: 'Paroh cu experiență de 10 ani',
      managerId: 'episcop1',
      manager: 'Episcopul Ioan'
    },
    {
      id: '2',
      nume: 'Ionescu',
      prenume: 'Ana',
      functie: 'Secretar',
      parohieId: 'parohia1',
      parohie: 'Biserica Sf. Nicolae',
      email: 'ana.ionescu@episcopia.ro',
      telefon: '0721234568',
      adresa: 'Str. Secundară nr. 5, București',
      dataAngajare: '2021-03-10',
      salariu: 2500,
      status: 'activ',
      specializare: 'Administrație',
      studii: 'Licență în Administrație',
      observatii: 'Secretar competent cu cunoștințe de contabilitate',
      managerId: '1',
      manager: 'Părintele Gheorghe'
    },
    {
      id: '3',
      nume: 'Marinescu',
      prenume: 'Maria',
      functie: 'Contabil',
      parohieId: 'parohia2',
      parohie: 'Biserica Sf. Gheorghe',
      email: 'maria.marinescu@episcopia.ro',
      telefon: '0721234569',
      adresa: 'Str. Tertiară nr. 10, București',
      dataAngajare: '2019-06-01',
      salariu: 3000,
      status: 'activ',
      specializare: 'Contabilitate',
      studii: 'Licență în Contabilitate',
      observatii: 'Contabil cu experiență în sectorul non-profit',
      managerId: 'episcop1',
      manager: 'Episcopul Ioan'
    },
    {
      id: '4',
      nume: 'Dumitrescu',
      prenume: 'Vasile',
      functie: 'Paroh',
      parohieId: 'parohia3',
      parohie: 'Biserica Sf. Maria',
      email: 'vasile.dumitrescu@episcopia.ro',
      telefon: '0721234570',
      adresa: 'Str. Patrară nr. 15, București',
      dataAngajare: '2018-09-15',
      salariu: 3200,
      status: 'inactiv',
      specializare: 'Teologie',
      studii: 'Master în Teologie',
      observatii: 'În concediu medical',
      managerId: 'episcop1',
      manager: 'Episcopul Ioan'
    },
    {
      id: '5',
      nume: 'Stoica',
      prenume: 'Ion',
      functie: 'Diacon',
      parohieId: 'parohia1',
      parohie: 'Biserica Sf. Nicolae',
      email: 'ion.stoica@episcopia.ro',
      telefon: '0721234571',
      adresa: 'Str. Cincisprezece nr. 20, București',
      dataAngajare: '2022-01-20',
      salariu: 2000,
      status: 'concediat',
      specializare: 'Teologie',
      studii: 'Licență în Teologie',
      observatii: 'Concediat la cererea proprie',
      managerId: '1',
      manager: 'Părintele Gheorghe'
    }
  ]);

  const statusOptions = ['activ', 'inactiv', 'concediat'];
  const functiiOptions = Array.from(new Set(angajati.map(a => a.functie)));

  const filteredAngajati = angajati.filter(angajat => {
    const fullName = `${angajat.nume} ${angajat.prenume}`.toLowerCase();
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
                         angajat.functie.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         angajat.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || angajat.status === filterStatus;
    const matchesFunctie = !filterFunctie || angajat.functie === filterFunctie;
    const matchesMyAngajati = !showOnlyMyAngajati || 
      (user?.role === 'paroh' && angajat.parohieId === user.parohieId) ||
      (user?.role === 'episcop' || user?.role === 'vicar');
    
    return matchesSearch && matchesStatus && matchesFunctie && matchesMyAngajati;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'activ': return 'success';
      case 'inactiv': return 'warning';
      case 'concediat': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'activ': return <ActiveIcon />;
      case 'inactiv': return <InactiveIcon />;
      case 'concediat': return <DeleteIcon />;
      default: return <PersonIcon />;
    }
  };

  const handleViewAngajat = (angajat: Angajat) => {
    setSelectedAngajat(angajat);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAngajat(null);
  };

  const handleEdit = (angajatId: string) => {
    // Implementare editare angajat
    console.log('Editare angajat:', angajatId);
  };

  const handleDelete = (angajatId: string) => {
    // Implementare ștergere angajat
    console.log('Ștergere angajat:', angajatId);
  };

  const TabPanel = ({ children, value, index }: { children: React.ReactNode; value: number; index: number }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  const totalAngajati = angajati.length;
  const angajatiActivi = angajati.filter(a => a.status === 'activ').length;
  const angajatiInactivi = angajati.filter(a => a.status === 'inactiv').length;
  const salariuMediu = angajati.reduce((sum, a) => sum + a.salariu, 0) / angajati.length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Angajați
        </Typography>
        {hasPermission('angajati.write') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* Implementare angajat nou */}}
          >
            Angajat nou
          </Button>
        )}
      </Box>

      {/* Statistici */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <GroupIcon color="primary" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Total angajați
                </Typography>
              </Box>
              <Typography variant="h5">
                {totalAngajati}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ActiveIcon color="success" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Activi
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                {angajatiActivi}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <InactiveIcon color="warning" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Inactivi
                </Typography>
              </Box>
              <Typography variant="h5" color="warning.main">
                {angajatiInactivi}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon color="info" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Salariu mediu
                </Typography>
              </Box>
              <Typography variant="h5" color="info.main">
                {salariuMediu.toFixed(0)} RON
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Toți angajații" />
          <Tab label="Activi" />
          <Tab label="Inactivi" />
          <Tab label="Concediați" />
        </Tabs>

        {/* Tab Toți angajații */}
        <TabPanel value={activeTab} index={0}>
          {/* Filtre */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Caută angajați..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">Toate</MenuItem>
                    {statusOptions.map(status => (
                      <MenuItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Funcție</InputLabel>
                  <Select
                    value={filterFunctie}
                    onChange={(e) => setFilterFunctie(e.target.value)}
                    label="Funcție"
                  >
                    <MenuItem value="">Toate</MenuItem>
                    {functiiOptions.map(functie => (
                      <MenuItem key={functie} value={functie}>
                        {functie}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterStatus('');
                    setFilterFunctie('');
                  }}
                >
                  Resetează
                </Button>
              </Grid>
            </Grid>
            {(user?.role === 'paroh' || user?.role === 'episcop' || user?.role === 'vicar') && (
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showOnlyMyAngajati}
                      onChange={(e) => setShowOnlyMyAngajati(e.target.checked)}
                    />
                  }
                  label="Arată doar angajații mei"
                />
              </Box>
            )}
          </Paper>

          {/* Tabel angajați */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Angajat</TableCell>
                  <TableCell>Funcție</TableCell>
                  <TableCell>Parohie</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Data angajare</TableCell>
                  <TableCell>Salariu</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAngajati.map((angajat) => (
                  <TableRow key={angajat.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            <Tooltip title={angajat.status}>
                              {getStatusIcon(angajat.status)}
                            </Tooltip>
                          }
                        >
                          <Avatar sx={{ width: 40, height: 40, mr: 2, bgcolor: 'primary.main' }}>
                            {angajat.prenume.charAt(0)}{angajat.nume.charAt(0)}
                          </Avatar>
                        </Badge>
                        <Box>
                          <Typography variant="subtitle2">
                            {angajat.prenume} {angajat.nume}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {angajat.specializare}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <WorkIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        {angajat.functie}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {angajat.parohie ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          {angajat.parohie}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
                          <Typography variant="body2">
                            {angajat.email}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
                          <Typography variant="body2">
                            {angajat.telefon}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        {angajat.dataAngajare}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {angajat.salariu} RON
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={angajat.status}
                        color={getStatusColor(angajat.status) as any}
                        size="small"
                        icon={getStatusIcon(angajat.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleViewAngajat(angajat)}
                        color="primary"
                      >
                        <ViewIcon />
                      </IconButton>
                      {hasPermission('angajati.write') && (
                        <IconButton 
                          onClick={() => handleEdit(angajat.id)}
                          color="secondary"
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      {hasPermission('angajati.delete') && (
                        <IconButton 
                          onClick={() => handleDelete(angajat.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab-uri pentru statusuri specifice */}
        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Angajați activi
          </Typography>
          <List>
            {angajati.filter(a => a.status === 'activ').map((angajat) => (
              <React.Fragment key={angajat.id}>
                <ListItem>
                  <ListItemIcon>
                    <ActiveIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${angajat.prenume} ${angajat.nume} - ${angajat.functie}`}
                    secondary={`${angajat.parohie || 'Fără parohie'} • Angajat din: ${angajat.dataAngajare} • ${angajat.salariu} RON`}
                  />
                  <Box>
                    <Chip
                      label={angajat.specializare}
                      color="primary"
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <IconButton
                      onClick={() => handleViewAngajat(angajat)}
                      color="primary"
                      size="small"
                    >
                      <ViewIcon />
                    </IconButton>
                  </Box>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" gutterBottom>
            Angajați inactivi
          </Typography>
          <List>
            {angajati.filter(a => a.status === 'inactiv').map((angajat) => (
              <React.Fragment key={angajat.id}>
                <ListItem>
                  <ListItemIcon>
                    <InactiveIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${angajat.prenume} ${angajat.nume} - ${angajat.functie}`}
                    secondary={`${angajat.parohie || 'Fără parohie'} • Angajat din: ${angajat.dataAngajare} • ${angajat.observatii}`}
                  />
                  <Box>
                    <Chip
                      label={angajat.specializare}
                      color="primary"
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <IconButton
                      onClick={() => handleViewAngajat(angajat)}
                      color="primary"
                      size="small"
                    >
                      <ViewIcon />
                    </IconButton>
                  </Box>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Typography variant="h6" gutterBottom>
            Angajați concediați
          </Typography>
          <List>
            {angajati.filter(a => a.status === 'concediat').map((angajat) => (
              <React.Fragment key={angajat.id}>
                <ListItem>
                  <ListItemIcon>
                    <DeleteIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${angajat.prenume} ${angajat.nume} - ${angajat.functie}`}
                    secondary={`${angajat.parohie || 'Fără parohie'} • Angajat din: ${angajat.dataAngajare} • ${angajat.observatii}`}
                  />
                  <Box>
                    <Chip
                      label={angajat.specializare}
                      color="primary"
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <IconButton
                      onClick={() => handleViewAngajat(angajat)}
                      color="primary"
                      size="small"
                    >
                      <ViewIcon />
                    </IconButton>
                  </Box>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </TabPanel>
      </Paper>

      {/* Dialog pentru vizualizare angajat */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalii angajat - {selectedAngajat?.prenume} {selectedAngajat?.nume}
        </DialogTitle>
        <DialogContent>
          {selectedAngajat && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Nume complet
                </Typography>
                <Typography variant="body1">
                  {selectedAngajat.prenume} {selectedAngajat.nume}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Funcție
                </Typography>
                <Typography variant="body1">
                  {selectedAngajat.functie}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body1">
                  {selectedAngajat.email}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Telefon
                </Typography>
                <Typography variant="body1">
                  {selectedAngajat.telefon}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Adresă
                </Typography>
                <Typography variant="body1">
                  {selectedAngajat.adresa}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Data angajare
                </Typography>
                <Typography variant="body1">
                  {selectedAngajat.dataAngajare}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Salariu
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {selectedAngajat.salariu} RON
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={selectedAngajat.status}
                  color={getStatusColor(selectedAngajat.status) as any}
                  icon={getStatusIcon(selectedAngajat.status)}
                />
              </Grid>
              {selectedAngajat.parohie && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Parohie
                  </Typography>
                  <Typography variant="body1">
                    {selectedAngajat.parohie}
                  </Typography>
                </Grid>
              )}
              {selectedAngajat.specializare && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Specializare
                  </Typography>
                  <Typography variant="body1">
                    {selectedAngajat.specializare}
                  </Typography>
                </Grid>
              )}
              {selectedAngajat.studii && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Studii
                  </Typography>
                  <Typography variant="body1">
                    {selectedAngajat.studii}
                  </Typography>
                </Grid>
              )}
              {selectedAngajat.manager && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Manager
                  </Typography>
                  <Typography variant="body1">
                    {selectedAngajat.manager}
                  </Typography>
                </Grid>
              )}
              {selectedAngajat.observatii && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Observații
                  </Typography>
                  <Typography variant="body1">
                    {selectedAngajat.observatii}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Închide</Button>
          {hasPermission('angajati.write') && (
            <Button
              onClick={() => handleEdit(selectedAngajat!.id)}
              color="primary"
              startIcon={<EditIcon />}
            >
              Editează
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Angajati;
